import { Anchor, Badge, Card, Pagination, Table, Text, Tooltip } from '@mantine/core';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { CampaignTypes } from '../../constants/pageTitles';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';
import { EDITOR_PATHS } from './AiDraftsList';

const PAGE_SIZE = 20;

const DECISIONS = {
  approved: { label: 'Approved', color: 'blue' },
  launched: { label: 'Launched', color: 'green' },
  rejected: { label: 'Rejected', color: 'red' },
};

const AiDraftDecisions = ({ type }) => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const [page, setPage] = useState(1);
  const [result, setResult] = useState();
  const [error, setError] = useState();

  const domainPath = `/accounts/${accountId}/domains/${domainId}`;

  useEffect(() => {
    let cancelled = false;
    setResult(undefined);
    setError(undefined);
    const query = queryString.stringify({ type, page, pageSize: PAGE_SIZE });
    fetch(`/api/admin${domainPath}/aiDrafts/decisions?${query}`)
      .then((data) => {
        if (!cancelled) setResult(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [fetch, domainPath, type, page]);

  let content;
  if (error) {
    content = <ErrorMessage message={getErrorMessage(error)} />;
  } else if (!result) {
    content = <Spinner />;
  } else if (!result.decisions?.length) {
    content = (
      <Text c="dimmed" size="sm">
        No decisions yet.
      </Text>
    );
  } else {
    content = (
      <>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Mode</Table.Th>
              <Table.Th>Decision</Table.Th>
              <Table.Th>Note</Table.Th>
              <Table.Th>Rationale</Table.Th>
              <Table.Th>Decided by</Table.Th>
              <Table.Th>When</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {result.decisions.map((decision) => (
              <Table.Tr key={decision.id}>
                <Table.Td>
                  {decision.campaignExists ? (
                    <Anchor
                      href={`#${domainPath}/${EDITOR_PATHS[type]}/${decision.campaignId}/details?mode=${decision.mode}`}
                      size="sm"
                    >
                      {decision.campaignName}
                    </Anchor>
                  ) : (
                    decision.campaignName
                  )}
                </Table.Td>
                <Table.Td>{CampaignTypes[decision.mode]}</Table.Td>
                <Table.Td>
                  <Badge variant="light" color={DECISIONS[decision.decision]?.color || 'gray'}>
                    {DECISIONS[decision.decision]?.label || decision.decision}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {decision.note ? (
                    <Text size="sm" maw={360}>
                      {decision.note}
                    </Text>
                  ) : (
                    <Text size="sm" c="dimmed">
                      No note
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Tooltip label={decision.rationale} multiline w={400} withArrow disabled={!decision.rationale}>
                    <Text size="sm" lineClamp={2} maw={360}>
                      {decision.rationale}
                    </Text>
                  </Tooltip>
                </Table.Td>
                <Table.Td>{decision.decidedBy?.email}</Table.Td>
                <Table.Td>{new Date(decision.decidedAt).toLocaleString()}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {result.total > PAGE_SIZE && (
          <Pagination total={Math.ceil(result.total / PAGE_SIZE)} value={page} onChange={setPage} mt="md" />
        )}
      </>
    );
  }

  return <Card withBorder>{content}</Card>;
};

export default AiDraftDecisions;
